import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { ServiceSiteKind, ServiceType } from '../../../__generated__/graphql';
import { authStore } from '../../../store/authStore';
import { modalActions } from '../../../store/authStore/modalStore';
import { Button } from '../../components/button';
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
} from '../../components/ui';
import { useMyServiceSitesQuery } from '../../services/serviceService/__generated__/queries.generated';
import { parseHandoverParams } from '../../utils/handoverLink';
import { SiteFormModal } from './SiteFormModal';
import { defaultServiceSearch, searchPath } from './serviceSearchState';
import { linkedVehicleSite } from './serviceSiteForm';

/**
 * The car site's "Book a handover" link lands here (spec 019 W27, T067):
 * `/services/book?linkedCarId&linkedBookingId&date&windowStart&windowEnd&serviceType`.
 * It finds the vehicle saved for that car (or adds it, linked to the car) and
 * opens the worker search for that window; the trip goes along to
 * `createJobIntent.linkedBookingId`.
 */
export default function HandoverStartPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useSnapshot(authStore);
  const link = parseHandoverParams(params);
  const sitesQuery = useMyServiceSitesQuery({
    skip: !isAuthenticated || !link,
    fetchPolicy: 'network-only',
  });
  const [adding, setAdding] = useState(false);
  const site =
    link && sitesQuery.data
      ? linkedVehicleSite(sitesQuery.data.myServiceSites, link.linkedCarId)
      : null;

  const goSearch = (siteId: string, nickname: string) => {
    if (!link) return;
    navigate(
      searchPath({
        ...defaultServiceSearch(),
        serviceType: ServiceType.VehicleHandover,
        where: nickname,
        siteId,
        date: link.date,
        windowStart: link.windowStart,
        windowEnd: link.windowEnd,
        linkedBookingId: link.linkedBookingId,
      }),
      { replace: true },
    );
  };

  useEffect(() => {
    if (site) goSearch(site.id, site.nickname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site?.id]);

  if (!link) {
    return (
      <PageContainer width="md">
        <Alert tone="error" title="This link is not complete">
          Open "Book a handover" again from your trip on the car site, or{' '}
          <Link to="/">search for a worker</Link>.
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer width="md" canvas data-testid="handover-start">
      <PageHeader
        eyebrow="Handover"
        title="Book a handover"
        description="A worker meets your guest at the car, hands over the keys and sends you photos."
      />
      {!isAuthenticated ? (
        <Card>
          <CardBody>
            <Stack $gap={4}>
              <Muted>Sign in with the same account you host with on the car site.</Muted>
              <div>
                <Button
                  type="button"
                  text="Sign in"
                  onClick={() => modalActions.openSignInModal(() => undefined)}
                  dataTestId="handover-sign-in"
                />
              </div>
            </Stack>
          </CardBody>
        </Card>
      ) : sitesQuery.loading || site ? (
        <LoadingBlock label="Finding your car" />
      ) : (
        <Card>
          <CardHeader
            title="Add your car"
            description="Save the car once, with where it is parked. Next time we find it for you."
          />
          <CardBody>
            <Button
              type="button"
              text="Add the car"
              onClick={() => setAdding(true)}
              dataTestId="handover-add-car"
            />
          </CardBody>
        </Card>
      )}
      {adding && (
        <SiteFormModal
          kind={ServiceSiteKind.Vehicle}
          initial={{ linkedCarId: link.linkedCarId }}
          onClose={() => setAdding(false)}
          onSaved={saved => goSearch(saved.id, saved.nickname)}
        />
      )}
    </PageContainer>
  );
}
